import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: t('nav.home'), path: '/' },
    { label: t('nav.structures'), path: '/structures' },
    { label: t('nav.portfolio'), path: '/portfolio' },
    { label: t('nav.about'), path: '/about' },
    { label: t('nav.contact'), path: '/contact' },
  ];

  const isOverlay = isHome && !scrolled;
  const textColor = isOverlay ? 'text-cream' : 'text-dark';
  const bg = isOverlay ? 'bg-transparent' : 'bg-cream border-b border-warm';

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${bg} ${textColor}`}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" className="flex items-baseline gap-2 group">
            <span className="font-serif-display text-3xl leading-none">TL</span>
            <span className="text-[10px] tracking-luxury uppercase text-gold-soft font-medium">
              Event Group
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.path === '/'}
                className={({ isActive }) =>
                  `text-[11px] tracking-luxury uppercase font-medium transition-colors duration-300 ${
                    isActive ? 'text-gold' : 'hover:text-gold'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-6">
            <div className="text-[11px] tracking-medium font-medium flex items-center gap-2">
              <button
                onClick={() => setLang('nl')}
                className={`transition-colors ${lang === 'nl' ? 'text-gold' : 'opacity-70 hover:opacity-100'}`}
              >
                NL
              </button>
              <span className="opacity-40">|</span>
              <button
                onClick={() => setLang('en')}
                className={`transition-colors ${lang === 'en' ? 'text-gold' : 'opacity-70 hover:opacity-100'}`}
              >
                EN
              </button>
            </div>
            <Link
              to="/contact"
              className={`text-[11px] tracking-luxury uppercase font-medium border px-7 py-3.5 transition-all duration-300 ${
                isOverlay
                  ? 'border-cream/70 text-cream hover:bg-cream hover:text-dark'
                  : 'border-dark text-dark hover:bg-dark hover:text-cream'
              }`}
            >
              {t('cta.quote')}
            </Link>
          </div>

          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-cream border-t border-warm">
          <nav className="flex flex-col px-6 py-6 space-y-5">
            {navLinks.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.path === '/'}
                className={({ isActive }) =>
                  `text-xs tracking-luxury uppercase font-medium ${
                    isActive ? 'text-gold' : 'text-dark'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setLang('nl')}
                className={`text-xs tracking-luxury uppercase font-medium ${lang === 'nl' ? 'text-gold' : 'text-dark/60'}`}
              >
                NL
              </button>
              <span className="text-dark/40">|</span>
              <button
                onClick={() => setLang('en')}
                className={`text-xs tracking-luxury uppercase font-medium ${lang === 'en' ? 'text-gold' : 'text-dark/60'}`}
              >
                EN
              </button>
            </div>
            <Link
              to="/contact"
              className="text-xs tracking-luxury uppercase font-medium border border-dark text-dark px-7 py-3.5 inline-block w-fit"
            >
              {t('cta.quote')}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
